import React, { useState, useEffect, useRef } from 'react';
import { Flag } from 'lucide-react';
import { fetchAllFeatureFlags, fetchFeatureFlag } from '../services/featureFlagsApi';
import FeatureFlagsList from './FeatureFlagsList';
import FeatureFlagDetails from './FeatureFlagDetails';
import FeatureFlagModal from './FeatureFlagModal';
import useIsMobile from '../hooks/useIsMobile';

const FeatureFlags = () => {
  const [flags, setFlags] = useState([]);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const detailsPanelRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isMobile = useIsMobile(768);

  useEffect(() => {
    loadFlags();
  }, []);

  const loadFlags = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAllFeatureFlags();
      setFlags(data);
    } catch (err) {
      const errorMessage = err.message || 'Failed to load feature flags. Please check if the backend is running.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFlagSelect = (flag) => {
    setSelectedFlag(flag);
    if (isMobile) {
      setIsModalOpen(true);
    } else if (detailsPanelRef.current) {
      detailsPanelRef.current.scrollTop = 0;
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleFlagUpdate = async () => {
    const updatedFlags = await fetchAllFeatureFlags();
    setFlags(updatedFlags);
    if (selectedFlag) {
      const selectedFlagId = selectedFlag.id || selectedFlag.Id;
      try {
        const updatedFlag = await fetchFeatureFlag(selectedFlagId);
        if (updatedFlag) setSelectedFlag(updatedFlag);
      } catch {
        const flagFromList = updatedFlags.find(f => (f.id || f.Id) === selectedFlagId);
        if (flagFromList) setSelectedFlag(flagFromList);
      }
    }
  };

  const filteredFlags = flags.filter(flag => {
    const flagId = (flag.id || flag.Id || '').toLowerCase();
    const flagDescription = (flag.description || flag.Description || '').toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    return flagId.includes(searchLower) || flagDescription.includes(searchLower);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-gray-500 dark:text-slate-400 text-lg">
        Loading feature flags...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-gray-600 dark:text-gray-400 p-8 text-center">
        <p className="mb-4">{error}</p>
        <button
          onClick={loadFlags}
          className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white mb-8 shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold mb-2">Feature Flags Management</h2>
            <p className="text-blue-100 opacity-90">Control feature rollouts and system capabilities across tenants in real-time.</p>
          </div>
          <input
            type="text"
            placeholder="Search flags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/70 focus:ring-2 focus:ring-white/50 focus:outline-none w-full md:w-64"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:h-[calc(100vh-280px)] lg:min-h-[400px]">
        <div className="lg:col-span-1 flex flex-col lg:min-h-0">
          <div className="flex items-center justify-between mb-2 shrink-0">
            <h3 className="font-semibold text-gray-700 dark:text-gray-300">All Flags ({filteredFlags.length})</h3>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto pr-2">
            <FeatureFlagsList
              flags={filteredFlags}
              selectedFlag={selectedFlag}
              onFlagSelect={handleFlagSelect}
            />
          </div>
        </div>
        <div
          ref={detailsPanelRef}
          className="lg:col-span-2 min-h-0 overflow-y-auto"
        >
          {selectedFlag && !isMobile ? (
            <FeatureFlagDetails flag={selectedFlag} onUpdate={handleFlagUpdate} />
          ) : !isMobile ? (
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 min-h-[400px] flex items-center justify-center text-center p-8">
              <div className="max-w-md">
                <div className="w-16 h-16 bg-blue-50 dark:bg-blue-950 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Flag className="w-8 h-8 text-blue-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Select a Feature Flag</h3>
                <p className="text-gray-500 dark:text-slate-400">Click on any flag from the list on the left to view its details, rollout percentage, and tenant overrides.</p>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {isMobile && (
        <FeatureFlagModal
          flag={selectedFlag}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onUpdate={handleFlagUpdate}
        />
      )}
    </div>
  );
};

export default FeatureFlags;
